import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/stores/auth';
import { useHasFeature } from '@/hooks/useHasFeature';
import { useIsMobile } from '@/hooks/useIsMobile';
import { isStoreBuild } from '@/lib/platform';
import { APP_DEVICE_NAME, IS_APP_BUILD } from '@/lib/appBrand';
import { disablePush, pushPermission, requestPushFromToggle } from '@/lib/push';
import { canHavePush } from '@/lib/pushAccess';
import { pushToggleState, type PushPermission } from '@/lib/pushToggle';
import { selfDeleteShown } from '@/lib/selfDelete';
import { openLegal, privacyFallback, privacyUrl, termsFallback, termsUrl } from '@/lib/legalLinks';
import { markUserLogout } from '@/lib/loginReturn';
import { useAppConfig } from '@/hooks/useAppConfig';
import { MBlock, MLabel, MRow, MSegmented, MSwitch, MTag } from '@/components/mobile/MobileKit';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { humanizeError } from '@/lib/errors';
import { setLanguage } from '@/app/i18n';
import { authApi } from '@/api/auth';
import { usersApi } from '@/api/users';
import { notificationsApi, type NotificationSettings } from '@/api/notifications';
import { hapticsEnabled, setHapticsEnabled, tapHeavy, tapMedium, notifySuccess } from '@/lib/haptics';
import { failedCount, flush, onOutboxChange, onOutboxFailed, pendingCount } from '@/lib/outbox';
import { OFFLINE_RECORDS_ANCHOR, useOfflineRecords, useScrollToOfflineRecords } from '@/hooks/useOfflineRecords';
import { useRecordQueue } from '@/hooks/useRecordQueue';
import { biometricsEnabled, checkBiometry, confirmIdentity, setBiometricsEnabled } from '@/lib/biometrics';
import {
  cancelDaily, loadPrefs, permissionGranted, requestPermission, savePrefs, scheduleDaily,
  type ReminderPrefs,
} from '@/lib/reminders';
import { ChangePasswordCard, EditProfileModal } from './StudentProfilePage';

/**
 * Параметрлер — отдельный экран настроек для телефона.
 *
 * ⚠️ ПОЧЕМУ ОТДЕЛЬНЫЙ ЭКРАН. В макете заказчика профиль — это шапка, три числа,
 * уровни по навыкам и тариф. Ничего больше. Настройки живут за шестерёнкой,
 * своим экраном, и собраны не карточками, а подписями разделов и строками.
 * Первая мобильная версия сваливала десктопные карточки под профиль — оттого
 * заказчик и сказал 24.08.2026, что профиль макету не соответствует.
 *
 * ⚠️ НИ ОДНА НАСТРОЙКА НЕ ПОТЕРЯНА. Экран собирали по списку десктопных
 * карточек: язык, уведомления, пароль, синхронизация, вход по лицу,
 * напоминания, отклик, выход, удаление. Проверку держит сторож
 * `profileEssentials.test.ts` — если строка исчезнет, тест упадёт.
 *
 * На широком экране этот адрес открывать нечему: там все настройки на своих
 * местах в десктопном профиле, поэтому уводим обратно.
 */
export function MobileSettingsPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const { hash } = useLocation();

  const saveLang = useMutation({
    mutationFn: (lang: string) => authApi.updateMe({ lang }),
    onSuccess: (u) => {
      setUser(u);
      qc.invalidateQueries({ queryKey: ['auth'] });
    },
  });
  const currentLang = (localStorage.getItem('lang') ??
    (user?.lang === 'kz' ? 'kk' : user?.lang) ??
    'ru') as 'kk' | 'ru' | 'en';

  // Якорь (`#offline-records` из подсказки у задания) уезжает вместе с переходом: на широком экране тумблер
  // живёт карточкой в профиле, и без якоря ссылка привела бы к началу страницы.
  if (!isMobile) return <Navigate to={{ pathname: '/app/profile', hash }} replace />;

  return (
    <div>
      <div className="flex items-center gap-2.5 pb-1 pt-1.5">
        <Link
          to="/app/profile"
          aria-label={t('nav.profile')}
          className="grid h-11 w-11 flex-none place-items-center rounded-full bg-surface-container-low text-on-surface"
        >
          <span className="material-symbols-outlined text-[18px]">chevron_left</span>
        </Link>
        <h1 className="flex-1 font-display text-[19px] font-bold text-on-surface">
          {t('profile.settingsTitle')}
        </h1>
      </div>

      <MLabel>{t('profile.interfaceLang')}</MLabel>
      <MSegmented
        tabs={[
          { key: 'kk', label: 'Қазақша' },
          { key: 'ru', label: 'Русский' },
          { key: 'en', label: 'English' },
        ]}
        active={currentLang}
        onChange={(lang) => {
          setLanguage(lang);
          saveLang.mutate(lang);
        }}
      />

      <NotificationsSection />

      {isStoreBuild() && <DeviceSection />}

      <MLabel>{t('profile.accountSection')}</MLabel>
      <MBlock>
        <MRow
          title={t('profile.editProfileTitle')}
          meta={user?.full_name ?? undefined}
          onClick={() => setEditing(true)}
        />
        <PasswordRow onOpen={() => setPasswordOpen(true)} />
        <SyncRow />
        <RecordsRow />
        <LogoutRow last />
      </MBlock>

      <DocumentsSection />

      <DeleteRow />

      {editing && <EditProfileModal onClose={() => setEditing(false)} />}
      <Modal
        open={passwordOpen}
        onClose={() => setPasswordOpen(false)}
        title={t('profile.changePassword')}
        size="sm"
      >
        <ChangePasswordCard />
      </Modal>
    </div>
  );
}

/* ─────────────────────────────── уведомления ─────────────────────────────── */

/**
 * ⚠️ В ПРИЛОЖЕНИИ — ОДИН ТУМБЛЕР «УВЕДОМЛЕНИЯ» (решения владельца 30.09 и 01.10.2026, договор `push.md` §3),
 * и только ученику с куратором. «Письма» и каналы — только на сайте: в приложении они на пуши не влияют
 * (пушем уходят два события), а тумблер, который ничего не меняет, — обещание впустую.
 * Сторож — `storeAbsent.test.ts`.
 */
function NotificationsSection() {
  return IS_APP_BUILD ? <AppPushSection /> : <SiteNotificationsSection />;
}

function AppPushSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  // Бесплатнику и сотруднику пушей нет — нет и тумблера (lib/pushAccess.ts).
  const allowed = isStoreBuild() && canHavePush(user);
  const [permission, setPermission] = useState<PushPermission | null>(null);
  const [asking, setAsking] = useState(false);

  const settings = useQuery({
    queryKey: ['notification-settings'],
    queryFn: notificationsApi.getSettings,
    enabled: allowed,
  });
  const save = useMutation({
    mutationFn: (next: NotificationSettings) => notificationsApi.updateSettings(next),
    onSuccess: (d) => qc.setQueryData(['notification-settings'], d),
  });

  useEffect(() => {
    if (!allowed) return;
    let alive = true;
    const read = () =>
      void pushPermission().then((p) => {
        if (alive) setPermission(p);
      });
    read();
    // Ученик сходил в Настройки телефона и вернулся — разрешение могло измениться.
    const onVisible = () => {
      if (document.visibilityState === 'visible') read();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [allowed]);

  if (!allowed || !permission) return null;

  const s = settings.data;
  const view = pushToggleState(permission, s?.push_enabled ?? false);

  const toggle = async () => {
    if (!s || asking) return;
    if (permission !== 'granted') {
      // Систему ещё не спрашивали: включение — это системное окно, а не наш флаг.
      setAsking(true);
      const answer = await requestPushFromToggle();
      setAsking(false);
      setPermission(answer);
      if (answer === 'granted' && !s.push_enabled) save.mutate({ ...s, push_enabled: true });
      return;
    }
    save.mutate({ ...s, push_enabled: !s.push_enabled });
  };

  return (
    <>
      <MLabel>{t('profile.notifications')}</MLabel>
      <MBlock>
        <MRow
          title={t('profile.notifications')}
          last
          trailing={
            <MSwitch
              label={t('profile.notifications')}
              checked={view.checked}
              disabled={view.disabled || !s || asking}
              onChange={() => void toggle()}
            />
          }
        />
      </MBlock>
      {view.hint && (
        <p className="px-1 pt-1.5 text-[11.5px] text-on-surface-variant">
          {t('push.deniedHint', { brand: APP_DEVICE_NAME })}
        </p>
      )}
      {save.error && <p className="px-1 pt-1.5 text-[11.5px] text-error">{humanizeError(save.error)}</p>}
    </>
  );
}

function SiteNotificationsSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: ['notification-settings'],
    queryFn: notificationsApi.getSettings,
  });
  const save = useMutation({
    mutationFn: (next: NotificationSettings) => notificationsApi.updateSettings(next),
    onSuccess: (d) => qc.setQueryData(['notification-settings'], d),
  });
  const s = settings.data;

  // ⚠️ Без права chat нет и тумблера «Сообщения в чате» (14.09.2026): чата у такого ученика нет,
  // и переключатель для него — намёк на закрытую часть.
  const canChat = useHasFeature('chat');
  // ⚠️ В ПРИЛОЖЕНИИ (26.09.2026, сборка 61) нет чата и разговорных комнат — нет и их переключателей, даже
  // если сервер назвал эти разделы: переключатель к отсутствующему разделу — тот же намёк на закрытое.
  const channelLabels: Partial<Partial<Record<keyof NotificationSettings['channels'], string>>> = {
    // В приложении расписания нет — подпись «Уроки» без «и расписание» (27.09.2026, техдолг 319).
    lessons: t(IS_APP_BUILD ? 'profile.notifLessonsApp' : 'profile.notifLessons'),
    homework: t('profile.notifHomework'),
    ...(canChat && !IS_APP_BUILD ? { chat: t('profile.notifChat') } : {}),
    // ⚠️ Переключателя «Платежи» здесь больше нет ВОВСЕ (01.09.2026). Раньше он
    // прятался только в магазинной сборке, а на сайте ученик его видел — и
    // проверяющий, заходящий туда тем же демо-доступом, тоже.
    ...(IS_APP_BUILD ? {} : { speaking: t('profile.notifSpeaking') }),
  };

  // ⚠️ В приложении из магазина канал «Оплаты» НЕ показываем.
  //
  // Оплата у нас происходит офлайн, в школе: в приложении нет ни цен, ни
  // покупок, ни ссылок на них — так решено ради правила Apple 3.1.1. А тумблер
  // с таким названием остался и намекает ревьюеру на оплату мимо Apple, то есть
  // ровно на то, чего мы избегали. Заметил заказчик 25.08.2026, разглядывая
  // собственную видеозапись для проверки.
  //
  // Настройка при этом никуда не девается: она остаётся в ответе сервера и на
  // сайте, где оплата уместна. Скрыт только вид.
  const visibleChannels = (Object.keys(channelLabels) as (keyof NotificationSettings['channels'])[])


  return (
    <>
      <MLabel>{t('profile.notifications')}</MLabel>
      <MBlock>
        {!s ? (
          <MRow title={t('common.loading')} last />
        ) : (
          <>
            <MRow
              title={t('profile.emailNotifications')}
              trailing={
                <MSwitch
                  label={t('profile.emailNotifications')}
                  checked={s.email_enabled}
                  onChange={() => save.mutate({ ...s, email_enabled: !s.email_enabled })}
                />
              }
            />
            {/* В приложении у пушей свой блок — `AppPushSection` выше; эта строка — только сайт. */}
            {IS_APP_BUILD ? null : (
              <MRow
                title={t('profile.pushNotifications')}
                trailing={
                  <MSwitch
                    label={t('profile.pushNotifications')}
                    checked={s.push_enabled}
                    onChange={() => save.mutate({ ...s, push_enabled: !s.push_enabled })}
                  />
                }
              />
            )}
            {visibleChannels.map(
              (key, i, all) => (
                <MRow
                  key={key}
                  title={channelLabels[key]}
                  last={i === all.length - 1}
                  trailing={
                    <MSwitch
                      label={channelLabels[key] ?? key}
                      checked={s.channels[key]}
                      onChange={() =>
                        save.mutate({ ...s, channels: { ...s.channels, [key]: !s.channels[key] } })
                      }
                    />
                  }
                />
              ),
            )}
          </>
        )}
      </MBlock>
      {save.error && <p className="px-1 pt-1.5 text-[11.5px] text-error">{humanizeError(save.error)}</p>}
    </>
  );
}

/* ───────────────── настройки самого устройства (только приложение) ────────── */

function DeviceSection() {
  const { t } = useTranslation();
  const [bio, setBio] = useState(biometricsEnabled);
  const [bioAvailable, setBioAvailable] = useState<boolean | null>(null);
  const [haptics, setHaptics] = useState(hapticsEnabled);
  const [prefs, setPrefs] = useState<ReminderPrefs>(loadPrefs);
  const [denied, setDenied] = useState(false);
  const offline = useOfflineRecords();
  useScrollToOfflineRecords(offline.visible);

  useEffect(() => {
    void checkBiometry().then((r) => setBioAvailable(r.available));
    void permissionGranted().then((ok) => setDenied(!ok && loadPrefs().daily));
  }, []);

  // Личность подтверждаем ДО включения: иначе человек включит защиту, не
  // проверив, что она срабатывает, и узнает об этом, уже будучи запертым.
  const toggleBio = async () => {
    if (!bio) {
      const passed = await confirmIdentity(t('biometrics.reason'), t('biometrics.usePassword'));
      if (!passed) return;
    }
    setBiometricsEnabled(!bio);
    setBio(!bio);
  };

  const updateReminders = async (next: ReminderPrefs) => {
    if (next.daily && !prefs.daily) {
      const ok = await requestPermission();
      if (!ok) {
        setDenied(true);
        return; // не включаем то, что всё равно не сработает
      }
      setDenied(false);
    }
    setPrefs(next);
    savePrefs(next);
    if (next.daily) {
      await scheduleDaily(next.dailyTime, t('reminders.dailyTitle'), t('reminders.dailyBody'));
    } else {
      await cancelDaily();
    }
  };

  return (
    <>
      <MLabel>{t('profile.deviceSection')}</MLabel>
      <MBlock>
        {bioAvailable !== false && (
          <MRow
            title={t('biometrics.title')}
            meta={t('biometrics.desc')}
            trailing={
              <MSwitch label={t('biometrics.title')} checked={bio} onChange={() => void toggleBio()} />
            }
          />
        )}
        <MRow
          title={t('reminders.daily')}
          meta={prefs.daily ? prefs.dailyTime : t('reminders.desc')}
          trailing={
            <MSwitch
              label={t('reminders.daily')}
              checked={prefs.daily}
              onChange={() => void updateReminders({ ...prefs, daily: !prefs.daily })}
            />
          }
        />
        {prefs.daily && (
          <MRow
            title={t('reminders.dailyTime')}
            trailing={
              <input
                type="time"
                value={prefs.dailyTime}
                onChange={(e) => void updateReminders({ ...prefs, dailyTime: e.target.value })}
                className="rounded-button bg-surface-container-high px-3 py-1.5 text-[13px] text-on-surface"
              />
            }
          />
        )}
        {/* «За час до занятия» — только сайт (27.09.2026, решение владельца): в приложении расписания нет, и
            напоминать не о чем. */}
        {IS_APP_BUILD ? null : (
          <MRow
            title={t('reminders.lessons')}
            trailing={
              <MSwitch
                label={t('reminders.lessons')}
                checked={prefs.lessons}
                onChange={() => void updateReminders({ ...prefs, lessons: !prefs.lessons })}
              />
            }
          />
        )}
        <MRow
          title={t('reminders.homework')}
          trailing={
            <MSwitch
              label={t('reminders.homework')}
              checked={prefs.homework}
              onChange={() => void updateReminders({ ...prefs, homework: !prefs.homework })}
            />
          }
        />
        {/* «Запись без связи» (решение владельца 02.10.2026): только когда включено на сервере и ученику уже
            встречалось задание с записью — `useOfflineRecords`. По умолчанию выключено. */}
        {offline.visible && (
          <MRow
            id={OFFLINE_RECORDS_ANCHOR}
            title={t('profile.offlineRecordsTitle')}
            meta={t('profile.offlineRecordsDesc')}
            trailing={
              <MSwitch label={t('profile.offlineRecordsTitle')} checked={offline.on} onChange={offline.toggle} />
            }
          />
        )}
        <MRow
          title={t('profile.hapticsTitle')}
          meta={t('profile.hapticsDesc')}
          last
          trailing={
            <MSwitch
              label={t('profile.hapticsTitle')}
              checked={haptics}
              onChange={() => {
                const next = !haptics;
                setHapticsEnabled(next);
                setHaptics(next);
                if (next) tapMedium(); // дать почувствовать, что именно включилось
              }}
            />
          }
        />
      </MBlock>
      {denied && <p className="px-1 pt-1.5 text-[11.5px] text-error">{t('reminders.denied')}</p>}
    </>
  );
}

/* ─────────────────────────────── строки аккаунта ─────────────────────────── */

function PasswordRow({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  // Пароля нет у тех, кто зарегистрировался по коду из письма: им предлагаем
  // задать, а не сменить.
  const hasPassword = user?.password_set !== false;
  return <MRow title={hasPassword ? t('profile.changePassword') : t('profile.setPassword')} onClick={onOpen} />;
}

function SyncRow() {
  const { t } = useTranslation();
  const [pending, setPending] = useState(0);
  const [busy, setBusy] = useState(false);
  // ⚠️ ОТКАЗАННОЕ — ТОЖЕ НЕ ОТПРАВЛЕНО (02.10.2026, дыра 5 сверки). Счётчик «ждут отправки» его не включает,
  // и профиль писал «Всё отправлено», пока красная полоса сверху говорила «не отправлено: 1».
  const [failed, setFailed] = useState(0);

  useEffect(() => {
    void pendingCount().then(setPending);
    void failedCount().then(setFailed);
    const offChange = onOutboxChange(setPending);
    const offFailed = onOutboxFailed(setFailed);
    return () => {
      offChange();
      offFailed();
    };
  }, []);

  const sync = async () => {
    setBusy(true);
    try {
      await flush();
      if ((await pendingCount()) === 0) notifySuccess();
    } finally {
      setBusy(false);
    }
  };

  const clean = pending === 0 && failed === 0;
  return (
    <MRow
      title={t('profile.syncTitle')}
      meta={
        pending > 0
          ? t('net.pending', { count: pending })
          : failed > 0
            ? t('net.failed', { count: failed })
            : t('profile.syncDone')
      }
      onClick={pending > 0 && !busy ? () => void sync() : undefined}
      trailing={clean ? <MTag tone="info">{t('profile.syncDone')}</MTag> : undefined}
    />
  );
}

// Неотправленные записи голоса — строка-вход на свой экран (промпт 22, 18.10.2026). Нет таких записей — строки нет:
// «Неотправленные работы» выше и так скажет «Всё отправлено».
function RecordsRow() {
  const { t } = useTranslation();
  const { rows, rejected } = useRecordQueue();
  const count = rows.length + rejected.length;
  if (count === 0) return null;
  return <MRow title={t('recordQueue.title')} meta={t('recordQueue.entryMeta', { count })} to="/app/profile/records" />;
}

function LogoutRow({ last }: { last?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const refreshToken = useAuthStore((s) => s.refreshToken);

  const leave = async () => {
    tapHeavy();
    // Метку устройства снимаем ДО сброса сессии и с ожиданием — иначе запрос уйдёт уже без ключа, и пуши
    // этого ученика достанутся следующему вошедшему. Ждём не дольше нескольких секунд (lib/push.ts).
    await disablePush();
    // Результата не ждём: «выйти» в самолёте должно срабатывать, иначе человек
    // остаётся в чужом аккаунте до посадки.
    if (refreshToken) authApi.logout(refreshToken).catch(() => {});
    // Вышел сам — без `?next`: следующий вход не должен открыть эти настройки (loginReturn.ts, 27.09.2026).
    markUserLogout();
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <MRow
      title={t('common.logout')}
      last={last}
      onClick={() => void leave()}
      trailing={
        <span className="material-symbols-outlined flex-none text-[17px] text-on-surface-variant">
          logout
        </span>
      }
    />
  );
}

/**
 * Документы — политика и условия (25.09.2026, сборка 61).
 *
 * ⚠️ ЗАЧЕМ. До этого ссылки на них были только под формой входа: вошедший ученик до политики
 * не добирался никак, а Apple требует, чтобы она открывалась из самого приложения (5.1.1(i)).
 * Адреса — только из `lib/legalLinks.ts`: сейчас это наши страницы, после Google Docs — их
 * адреса, и меняется это там одной строкой. Сторож `legalLinks.test.ts`.
 */
function DocumentsSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <>
      <MLabel>{t('profile.documentsSection')}</MLabel>
      <MBlock>
        {/* iOS-приложение — Google Docs из карточки App Store, Android и сайт — свои страницы (lib/legalLinks.ts). */}
        <MRow title={t('profile.privacyPolicy')} onClick={() => void openLegal(privacyUrl(), navigate, privacyFallback())} />
        <MRow title={t('profile.termsOfUse')} onClick={() => void openLegal(termsUrl(), navigate, termsFallback())} last />
      </MBlock>
    </>
  );
}

/**
 * ⚠️ НА САЙТЕ СКРЫТА, В ПРИЛОЖЕНИИ ИЗ МАГАЗИНА — ВСЕГДА (D37, 12.09.2026): правило и
 * причины — `lib/selfDelete.ts`. Эта страница открывается и в телефонном браузере,
 * поэтому условие нужно и здесь. Экспортирована ради `selfDeleteHidden.test.ts`.
 */
export function DeleteRow() {
  useAppConfig();
  if (!selfDeleteShown()) return null;
  return <DeleteRowBody />;
}

function DeleteRowBody() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const [confirming, setConfirming] = useState(false);

  const del = useMutation({
    mutationFn: async () => {
      tapHeavy();
      // Метку устройства — до удаления: удаление учётки на сервере метки не чистит (договор push.md §8).
      await disablePush();
      return usersApi.deleteMyAccount();
    },
    onSuccess: () => {
      markUserLogout();
      logout();
      navigate('/login', { replace: true });
    },
  });

  return (
    <>
      <button
        type="button"
        onClick={() => {
          del.reset();
          setConfirming(true);
        }}
        className="mt-3.5 flex w-full items-center gap-3 rounded-[22px] px-4 py-3 text-error"
      >
        <span className="material-symbols-outlined text-[17px]">delete_forever</span>
        <span className="text-[13px] font-semibold">{t('profile.deleteBtn')}</span>
      </button>

      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title={t('profile.deleteConfirmTitle')}
        size="sm"
      >
        {/* ⚠️ Без галочки «понимаю, что необратимо» (D71, 25.09.2026): удаление мягкое, учётку
            возвращает администратор — галочка утверждала неправду. Текст окна утверждён владельцем. */}
        <p className="text-sm text-on-surface-variant">{t('profile.deleteConfirmDesc')}</p>
        {del.error && <p className="mt-3 text-sm text-error">{humanizeError(del.error)}</p>}
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setConfirming(false)} disabled={del.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" loading={del.isPending} onClick={() => del.mutate()}>
            {t('profile.deleteConfirmBtn')}
          </Button>
        </div>
      </Modal>
    </>
  );
}
